import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@material-ui/core';
import Header from 'components/Headers/Header.js';
import { useEffect, useMemo, useState } from 'react';
import { AutoSizer, Column, Table, TableCellProps } from 'react-virtualized';
import {
  Card,
  CardHeader,
  Col,
  Container,
  Row,
  Button,
  Badge,
} from 'reactstrap';
import api from 'services/api';
import Swal from 'sweetalert2';
import { BuscarRegras } from '../Regras/styles';
import './styles.css';

interface RetornoRegras {
  agrupador: string;
  material: {
    id: number;
    descricao: string;
    diteCodigo: string;
    codigoSAP: string;
  };
  regra: {
    idRegra: number;
    regraAplicacao: string;
    regraAplicacaoOriginal: string;
    regraQuantidade: string;
    regraQuantidadeOriginal: string;
  };
  validacao: {
    valido: boolean;
    quantidade: number;
  };
}

type CellProps = Omit<TableCellProps, 'rowData'> & {
  // eslint-disable-next-line react/no-unused-prop-types
  rowData: RetornoRegras;
};

const ValidarRegras: React.FC = () => {
  const [configuracaoId, setConfiguracaoId] = useState<number | string | null>(
    null,
  );
  const [isLoadingBuscaValidacoes, setIsLoadingBuscaValidacoes] =
    useState(false);

  const [listaRegras, setListaRegras] = useState<RetornoRegras[]>([]);

  const [filtroStatus, setFiltroStatus] = useState<
    'todos' | 'validos' | 'invalidos'
  >('todos');
  const [search, setSearch] = useState('');

  async function validacaoRegras() {
    if (configuracaoId === '') return;
    setIsLoadingBuscaValidacoes(true);
    try {
      Swal.fire({
        icon: 'info',
        title: 'Buscando dados',
        text: `O processo pode demorar um pouco`,
        allowOutsideClick: false,
        showConfirmButton: false,
      });
      const retorno = await api.post(`/regra-item/valida-regras-relatorio`, {
        orcamentoVersaoId: configuracaoId,
      });

      setListaRegras(retorno.data);
      Swal.close();

      if (retorno.data.length === 0) {
        Swal.fire({
          icon: 'error',
          title: 'Ops!',
          text: `Configuração não existe`,
        });
      }
    } catch (error) {
      // console.log(error);
      Swal.fire({
        icon: 'error',
        title: 'Ops!',
        text: `Houve um erro na busca`,
        allowOutsideClick: false,
        showConfirmButton: false,
      });
      Swal.close();
    } finally {
      setIsLoadingBuscaValidacoes(false);
    }
  }

  const handleChange = (event: any) => {
    setFiltroStatus(event.target.value);
  };

  function filtroTabela(
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) {
    setSearch(event.target.value);
  }

  const listaFiltrada = useMemo(() => {
    let lista = [...listaRegras];

    if (filtroStatus === 'validos') {
      lista = lista.filter(item => item.validacao?.valido === true);
    }

    if (filtroStatus === 'invalidos') {
      lista = lista.filter(item => item.validacao?.valido === false);
    }

    if (search.trim() !== '') {
      const termo = search.toUpperCase();

      lista = lista.filter(
        item =>
          item.agrupador?.toUpperCase().includes(termo) ||
          item.material?.descricao?.toUpperCase().includes(termo) ||
          item.material.diteCodigo.toUpperCase().includes(termo) ||
          item.regra.idRegra.toString().includes(termo),
      );
    }

    return lista;
  }, [listaRegras, filtroStatus, search]);

  return (
    <>
      <Header showCards={false} />
      <Container className="mt--7" fluid>
        <Card className="shadow">
          <CardHeader className="border-0">
            <Row>
              <Col sm="2">
                <h2 className="mb-0">Validar regras</h2>
              </Col>
              <Col sm="2">
                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="filtro-label">Mostrar</InputLabel>
                  <Select
                    labelId="filtro-label"
                    value={filtroStatus}
                    onChange={handleChange}
                    label="Mostrar"
                  >
                    <MenuItem value="todos">Tudo</MenuItem>
                    <MenuItem value="validos">Válidos</MenuItem>
                    <MenuItem value="invalidos">Inválidos</MenuItem>
                  </Select>
                </FormControl>
              </Col>

              <Col sm="4" style={{ display: 'flex', justifyContent: 'center' }}>
                <BuscarRegras
                  id="outlined-basic"
                  label="Filtrar lista"
                  variant="outlined"
                  size="small"
                  type="text"
                  // hidden={!exibeCampoBusca}
                  onChange={event => {
                    filtroTabela(event);
                  }}
                />
              </Col>
              <Col
                sm="4"
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                }}
              >
                <TextField
                  fullWidth
                  label="Código da configuração"
                  size="small"
                  variant="outlined"
                  id="outlined-basic"
                  value={configuracaoId}
                  type="number"
                  onChange={event => {
                    const { value } = event.target;
                    setConfiguracaoId(value === '' ? '' : Number(value));
                  }}
                />
                <Button
                  className="ml-2"
                  disabled={isLoadingBuscaValidacoes}
                  color="primary"
                  type="button"
                  onClick={validacaoRegras}
                >
                  Buscar
                </Button>
              </Col>
            </Row>
          </CardHeader>

          <div
            id="list-container"
            style={{
              width: '100%',
              height: '100vh',
              overflow: 'hidden',
              textAlign: 'center',
            }}
          >
            <AutoSizer>
              {({ width, height }) => (
                <Table
                  key={listaRegras.length}
                  width={width}
                  height={height}
                  headerHeight={40}
                  rowHeight={70}
                  rowCount={listaFiltrada.length}
                  rowGetter={({ index }) => listaFiltrada[index]}
                  rowStyle={{
                    borderBottom: '1px solid #ccc',
                    fontSize: '0.9rem',
                  }}
                >
                  <Column
                    label="Regra ID"
                    dataKey="id"
                    width={100}
                    cellRenderer={({ rowData }: CellProps) => (
                      <div>{rowData.regra.idRegra}</div>
                    )}
                  />

                  <Column
                    label="Regra"
                    dataKey="regra"
                    width={600}
                    cellRenderer={({ rowData }: CellProps) => (
                      <>
                        <Tooltip
                          title={
                            <>
                              <strong>Regra aplicação: </strong>
                              {rowData.regra.regraAplicacao}
                            </>
                          }
                          classes={{ tooltip: 'tooltipCustom' }}
                        >
                          <div>
                            <strong>Regra aplicação:</strong>{' '}
                            {rowData.regra.regraAplicacao}
                          </div>
                        </Tooltip>
                        <Tooltip
                          title={
                            <>
                              <strong>Regra Qnt:</strong>{' '}
                              {rowData.regra.regraAplicacao}
                            </>
                          }
                          classes={{ tooltip: 'tooltipCustom' }}
                        >
                          <div>
                            <strong>Regra Qnt:</strong>{' '}
                            {rowData.regra.regraAplicacao}
                          </div>
                        </Tooltip>
                      </>
                    )}
                  />
                  <Column
                    label="Agrupador"
                    dataKey="agrupador"
                    width={200}
                    cellRenderer={({ rowData }: CellProps) => (
                      <div>{rowData.agrupador}</div>
                    )}
                  />

                  <Column
                    label="Descrição"
                    dataKey="descricao"
                    width={500}
                    cellRenderer={({ rowData }: CellProps) => (
                      <Tooltip
                        title={rowData.material.descricao}
                        classes={{ tooltip: 'tooltipCustom' }}
                      >
                        <div>
                          <strong>{rowData.material.diteCodigo ?? ''}</strong> -{' '}
                          {rowData.material.descricao}
                        </div>
                      </Tooltip>
                    )}
                  />
                  <Column
                    label="Válido"
                    dataKey="valido"
                    width={150}
                    cellRenderer={({ rowData }: CellProps) => (
                      <div>
                        {rowData.validacao.valido ? (
                          <Badge color="success">Sim</Badge>
                        ) : (
                          <Badge color="warning">Não</Badge>
                        )}
                      </div>
                    )}
                  />
                  <Column
                    label="Qnt. Válidos"
                    dataKey="Quantidade válida de materiais a serem inclusos."
                    width={150}
                    headerRenderer={() => (
                      <div title="Quantidade válida de materiais a serem inclusos.">
                        Qnt. Válidos
                      </div>
                    )}
                    cellRenderer={({ rowData }) => (
                      <div>{rowData.validacao.quantidade}</div>
                    )}
                  />
                </Table>
              )}
            </AutoSizer>
          </div>
        </Card>
      </Container>
    </>
  );
};

export default ValidarRegras;
